import { computed, toValue, unref, type MaybeRefOrGetter } from 'vue';
import { canonicalUrl, SEO_DEFAULT_IMAGE, SEO_SITE_NAME, seoDescription, serializeJsonLd } from '@/utils/seo';

export interface HeadInput {
    title?: string;
    description?: string;
    /** Route path; becomes the canonical URL and og:url (query and hash dropped). */
    path?: string;
    /** Absolute image URL. Omitted = the generic 1200x630 share card. */
    image?: string;
    imageWidth?: number;
    imageHeight?: number;
    imageAlt?: string;
    noindex?: boolean;
    /** Overrides og:url when the shareable URL must keep its query (shared itineraries). */
    url?: string;
    /** schema.org object(s), rendered as application/ld+json. */
    jsonLd?: object | object[] | null;
}

/**
 * Title, description, canonical, Open Graph, Twitter card and JSON-LD for a
 * page. Accepts a plain object or a ref/getter, so a layout can follow the
 * route as it changes without being re-run.
 *
 * Twitter's tags use `name=` (they were `property=`, which X ignores), and a
 * summary_large_image card needs twitter:image as well as the og tags.
 */
export function updateHead(input: MaybeRefOrGetter<HeadInput>) {
    // Fields are unwrapped one level as well: callers have passed a computed
    // as a field value (`description: desc`), which used to reach the meta
    // tag as an object and drop the description altogether.
    const page = computed<HeadInput>(() => {
        const raw = (toValue(input) || {}) as Record<string, unknown>;
        return Object.fromEntries(Object.entries(raw).map(([key, value]) => [key, unref(value)])) as HeadInput;
    });

    useHead(
        computed(() => {
            const p = page.value;
            const title = p.title || '';
            // Plain text cut at a word boundary, whatever the caller passed.
            const description = seoDescription(p.description);
            const canonical = canonicalUrl(p.path);
            // The old portrait card (and its byte-identical itinerary copy) is
            // swapped for the landscape one wherever a template still names it;
            // at 1080x1350 previews cropped the festival dates off the bottom.
            // Any other caller-supplied image without dimensions is of unknown
            // shape, so width/height are only stated when we actually know them.
            const usingDefault = !p.image || /\/img\/ohc-share(-itinerary)?\.jpg$/.test(p.image);
            const image = usingDefault ? SEO_DEFAULT_IMAGE.url : p.image!;
            const width = usingDefault ? SEO_DEFAULT_IMAGE.width : p.imageWidth;
            const height = usingDefault ? SEO_DEFAULT_IMAGE.height : p.imageHeight;
            const alt = p.imageAlt || (usingDefault ? SEO_DEFAULT_IMAGE.alt : title);
            const jsonLd = p.jsonLd ? (Array.isArray(p.jsonLd) ? p.jsonLd : [p.jsonLd]) : [];

            const meta: Record<string, string>[] = [
                { name: 'description', content: description },
                { name: 'twitter:card', content: 'summary_large_image' },
                { name: 'twitter:title', content: title },
                { name: 'twitter:description', content: description },
                { name: 'twitter:image', content: image },
                { name: 'twitter:image:alt', content: alt },
                { property: 'og:site_name', content: SEO_SITE_NAME },
                { property: 'og:title', content: title },
                { property: 'og:description', content: description },
                { property: 'og:type', content: 'website' },
                { property: 'og:url', content: p.url || canonical },
                { property: 'og:locale', content: 'en_US' },
                { property: 'og:image', content: image },
                { property: 'og:image:alt', content: alt },
                ...(width && height
                    ? [
                          { property: 'og:image:width', content: String(width) },
                          { property: 'og:image:height', content: String(height) },
                      ]
                    : []),
            ];
            if (p.noindex) meta.push({ name: 'robots', content: 'noindex' });

            return {
                title,
                meta,
                link: [{ rel: 'canonical', href: canonical }],
                script: jsonLd.map((data, i) => ({
                    key: `ld-json-${i}`,
                    type: 'application/ld+json',
                    innerHTML: serializeJsonLd(data),
                })),
            };
        }),
    );
}
